import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { useNavigate } from "react-router-dom";
import { FolderKanban, Users, Gauge, UserMinus, Plus, Download } from "lucide-react";
import PageHeader from "../common/PageHeader";
import StatsCards, { StatCardConfig } from "../common/StatsCards";
import Button from "../common/Button";
import { useGetResourceAllocationsQuery, useGetResourceStatsQuery, useLazyDownloadResourceReportQuery } from "../../store/apis/resource-allocation/resource-allocation.api";
import { useGetUsersForFilterQuery } from "../../store/apis/user.api";
import { useHasPermission } from "../../store/hooks/useRbac";
import { DEBOUNCE_DELAYS, useDebounce } from "../../utils/debounce";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import { countUnderAllocated } from "../../utils/resourceStats";
import ResourceAllocationModal from "./ResourceAllocationModal";
import ResourceAllocationTable, { ResourceAllocationTableProps } from "./ResourceAllocationTable";
import type { ResourceRecord } from "./ResourceTableSection";

const statCards: StatCardConfig[] = [
  {
    key: "allocated",
    label: "Allocated",
    icon: Users,
    iconBg: "var(--color-primary-100)",
    iconColor: "var(--color-primary-600)",
  },
  {
    key: "activeProjects",
    label: "Active Projects",
    icon: FolderKanban,
    iconBg: "var(--color-info-light)",
    iconColor: "var(--color-info)",
  },
  {
    key: "avgUtilization",
    label: "Avg Utilization",
    icon: Gauge,
    iconBg: "var(--color-success-light)",
    iconColor: "var(--color-success-dark)",
    suffix: "%",
    formatter: (v) => Math.round(v),
  },
  {
    key: "underAllocated",
    label: "Under-allocated",
    icon: UserMinus,
    iconBg: "var(--color-error-light)",
    iconColor: "var(--color-error-dark)",
  },
];

const ResourceManagement = () => {
  const navigate = useNavigate();
  const canViewResource = useHasPermission(PERMISSIONS.RESOURCE_MANAGEMENT_VIEW);
  const canManageResource = useHasPermission(PERMISSIONS.RESOURCE_MANAGEMENT_MANAGE);

  const { data: stats, isLoading: isStatsLoading, error: statsError } = useGetResourceStatsQuery();
  const { data: rawEmployees } = useGetUsersForFilterQuery({ includeInactive: false });

  const [filters, setFilters] = useState({
    search: "",
    resourceIds: [] as string[],
    startDate: "",
    endDate: "",
    isAllocated: "" as string,
  });

  const debouncedSearch = useDebounce(filters.search, DEBOUNCE_DELAYS.SEARCH);

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [openAllocateModal, setOpenAllocateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  const employeeOptions = useMemo(() => {
    const list = Array.isArray(rawEmployees) ? rawEmployees : [];
    return list.map((u: any) => ({
      label: u.fullName,
      value: u.id,
    }));
  }, [rawEmployees]);

  useEffect(() => {
    if (statsError) {
      toast.error("Failed to load resource stats.");
    }
  }, [statsError]);

  const queryParams = useMemo(() => {
    let isAllocatedParam: boolean | undefined = undefined;
    if (filters.isAllocated === "true") isAllocatedParam = true;
    if (filters.isAllocated === "false") isAllocatedParam = false;

    return {
      page,
      limit,
      search: debouncedSearch || undefined,
      userIds: filters.resourceIds.length > 0 ? filters.resourceIds : undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      isAllocated: isAllocatedParam,
    };
  }, [page, limit, debouncedSearch, filters]);

  const {
    data,
    isFetching: isFetchingResource,
  } = useGetResourceAllocationsQuery(queryParams);

  const resources = data?.data || [];
  const total = data?.total || 0;

  const [triggerDownload, { isFetching: isDownloading }] = useLazyDownloadResourceReportQuery();

  const handleDownload = async () => {
    try {
      const params = {
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        search: filters.search?.trim() || "",
        userIds: filters.resourceIds.length > 0 ? filters.resourceIds : undefined,
      };

      const blob = await triggerDownload(params).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Resource_Allocation_Report_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Report downloaded successfully");
    } catch (err) {
      console.error("Download Error:", err);
      toast.error("Failed to download report");
    }
  };

  const handleView = (resource: ResourceRecord) => {
    navigate(
      `/resource-allocation/resource-management/resource-details/${resource._id}`,
    );
  };

  const handleAssign = (resource: ResourceRecord) => {
    setSelectedUser(resource._id);
    setOpenAllocateModal(true);
  };

  const tableProps: ResourceAllocationTableProps = {
    resources,
    loading: isFetchingResource,
    total,
    currentPage: page,
    itemsPerPage: limit,
    onPageChange: setPage,
    onItemsPerPageChange: setLimit,
    onView: canViewResource ? handleView : undefined,
    onAssign: canManageResource ? handleAssign : undefined,
    search: filters.search,
    onSearchChange: (v) => handleFilterChange("search", v),
    resourceIds: filters.resourceIds,
    onResourceChange: (v) => handleFilterChange("resourceIds", v),
    isAllocated: filters.isAllocated,
    onAllocatedChange: (v) => handleFilterChange("isAllocated", v),
    startDate: filters.startDate,
    endDate: filters.endDate,
    onStartDateChange: (v) => handleFilterChange("startDate", v),
    onEndDateChange: (v) => handleFilterChange("endDate", v),
    resourcesList: employeeOptions,
  };

  const statsValues = useMemo(
    () => ({
      allocated: stats?.allocatedResources ?? 0,
      activeProjects: stats?.totalProjects ?? 0,
      avgUtilization: stats?.avgAllocation ?? 0,
      underAllocated: countUnderAllocated(resources),
    }),
    [stats, resources],
  );

  return (
    <div className="w-full">
      <PageHeader
        title="Resource Allocation"
        description="Allocation matrix across active projects"
        breadcrumbs={[{ label: "Home", path: "/dashboard" }, { label: "Resource Mgmt" }]}
        className="mb-5 sm:mb-6"
        actions={
          <>
            <Button
              appearance="secondary2"
              onClick={handleDownload}
              loading={isDownloading}
              icon={<Download className="w-4 h-4" />}
            >
              Export
            </Button>
            {canManageResource && (
              <Button
                appearance="primary"
                onClick={() => {
                  setSelectedUser(null);
                  setOpenAllocateModal(true);
                }}
                icon={<Plus className="w-4 h-4" />}
              >
                Allocate
              </Button>
            )}
          </>
        }
      />

      <div className="mb-5 sm:mb-6">
        <StatsCards
          variant="simple-icon"
          valueOnTop
          stats={statsValues as unknown as Record<string, unknown>}
          cards={statCards}
          loading={isStatsLoading}
          containerClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
        />
      </div>

      <ResourceAllocationTable {...tableProps} />

      {openAllocateModal && (
        <ResourceAllocationModal
          mode="both"
          userId={selectedUser ?? undefined}
          onClose={() => setOpenAllocateModal(false)}
          onSave={() => setOpenAllocateModal(false)}
        />
      )}
    </div>
  );
};

export default ResourceManagement;